
import { select, Selection } from "d3-selection";
import { symbol } from "d3-shape";
import { symbolDiamond } from "d3";
import { Vue } from "vue-class-component";
import { Prop, Ref } from "vue-property-decorator";
import { GetPersonalityMatrix, GetProfile } from "../../types";
/* eslint-disable @typescript-eslint/no-explicit-any */

export default class PersonalityMatrixChart extends Vue {
  @Ref("container") readonly container!: HTMLElement;
  @Prop({ type: Array, required: true }) chartData!: (GetPersonalityMatrix & GetProfile)[];
  @Prop({ type: Number }) size?: number;
  g!: Selection<SVGGElement, unknown, null, undefined>;
  coords?: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    cx: number;
    cy: number;
  };
  draw() {
    const PentagonChart = {
      draw: (id: any, d: any, options: any) => {
        const cfg: any = Object.assign(
          {
            w: 300,
            h: 300,
            factor: 1,
            radians: 2 * Math.PI,
            margin: 150
          },
          options
        );
        const allAxis = ["", "", "", "", ""];
        const total = allAxis.length;
        select(id)
          .select("svg")
          .remove();
        const svg = select(id)
          .append("svg")
          .attr("width", cfg.w + cfg.margin * 2 + 100)
          .attr("height", cfg.h + cfg.margin * 1.5);
        const vert = 2;
        const pgg = svg.append("g").attr("transform", "translate(150, 143)");
        const pg = pgg
          .append("g")
          .attr("class", "pg")
          .style("transform-box", "fill-box")
          .style("transform-origin", "center")
          .attr("transform", `rotate(180) scale(1.56)`);
        pg.selectAll("polygon")
          .data(allAxis)
          .enter()
          .append("g")
          .attr("class", "polygon")
          .append("polygon")
          .attr("points", (d, i) => {
            const x1 = (cfg.w / vert) * (1 - cfg.factor * Math.sin((i * cfg.radians) / total));
            const x2 = (cfg.w / vert) * (1 - cfg.factor * Math.sin(((i + 1) * cfg.radians) / total));
            const y1 = (cfg.h / vert) * (1 - cfg.factor * Math.cos((i * cfg.radians) / total));
            const y2 = (cfg.h / vert) * (1 - cfg.factor * Math.cos(((i + 1) * cfg.radians) / total));
            return `${x1},${y1} ${x2},${y2} ${cfg.w / vert} ${cfg.h / vert} ${x1},${y1}`;
          })
          .attr("class", "line")
          .style("fill", (d, i) => ["#ff80ab", "#9575cd", "#b9f6ca", "#fff176", "#81d4fa"][i]);
        const g = pgg.append("g").attr("transform", "translate(0, 0)");
        const coods = {
          cx: pg.node()!.getBBox().width / 2 + pg.node()!.getBBox().x,
          cy: pg.node()!.getBBox().height - cfg.w / 2
        };
        this.g = g;
        return coods;
      }
    };
    this.coords = PentagonChart.draw(this.container, null, {
      w: this.size || Math.min(this.container.clientWidth, this.container.clientHeight),
      h: this.size || Math.min(this.container.clientWidth, this.container.clientHeight)
    });
  }
  plot() {
    this.g.selectAll("g").remove();
    this.g.selectAll("text").remove();
    if (!this.chartData || !this.chartData.length) {
      return;
    }
    const circles: any = [];
    this.g
      .append("g")
      .selectAll(".nodes")
      .data(this.chartData)
      .enter()
      .append("path")
      .attr(
        "d",
        symbol()
          .type(symbolDiamond)
          .size(50)
      )
      // shrink a little by multiplying .8 so that points can be sure that there are in the pentagon as possible
      // and expand by multiplying pentagon scale
      // 2.5 means r / 2 so it can not be misaligned from center
      .attr("transform", d => {
        const x = this.coords!.cx + ((d.per_mx * 0.8 * 1.3) / 100) * this.coords!.cx + 2.5;
        const y = this.coords!.cy + ((-d.per_my * 0.8 * 1.3) / 100) * this.coords!.cy - 2.5;
        circles.push({ x, y });
        return "translate(" + x + "," + y + ")";
      })
      .style("stroke", "black")
      .style("fill", d => {
        return d.subtype ? "white" : "black";
      });

    circles.forEach((circle: any, i: number) => {
      this.g
        .append("text")
        .attr("x", circle.x + 7)
        .attr("y", circle.y + 4)
        .style(
          "fill",
          this.chartData[i].id.match(/^APL#.+?#$/)
            ? "#ff6d00"
            : this.chartData[i].id.match(/^.+#retired#$/)
            ? "#4a4a4a"
            : "#2962ff"
        )
        .text(`${this.chartData[i].lastName} ${this.chartData[i].firstName}`);
    });
  }
  updated() {
    this.plot();
  }
  mounted() {
    this.draw();
    this.plot();
  }
}
/* eslint-enable */
